import { useMemo } from "react";
import {
  DownloadIcon,
  CalendarClock,
  XCircle,
  EllipsisVertical,
  FlaskConical,
} from "lucide-react";
import moment from "moment";
import { OrderHistoryItemData } from "src/types/orderHistory.types";
import OrderHistory from "src/constants/orderHistory";
import Card from "src/components/app/Card/Card";
import Popover from "src/components/common/Popover";
import { useNavigate, useParams } from "react-router-dom";

const ORDER_STATUS = {
  COMPLETED: "COMPLETED",
  UPCOMING: "UPCOMING",
  CANCELLED: "CANCELLED",
  BOOKED: "BOOKED",
  PENDING: "PENDING",
  RESCHEDULED: "RESCHEDULED",
};

const REFUND_STATUS = {
  COMPLETED: "COMPLETED",
  IN_PROGRESS: "IN_PROGRESS",
};

const CUSTOME_STATUS = {
  REFUND_COMPLETED: "Refund Completed",
  REFUND_IN_PROGRESS: "Refund In Progress",
};

const VENDOR_FLG = "C";

const OrderHistoryItem = ({
  item,
  onPressItem,
  onPressMenuButton,
  onDownloadReportPress,
}: {
  item: OrderHistoryItemData;
  onPressItem: (item: OrderHistoryItemData) => void;
  onPressMenuButton: (item: OrderHistoryItemData) => void;
  onDownloadReportPress: (item: OrderHistoryItemData) => void;
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];

  const showMenuButton = useMemo(() => {
    return item?.vendor_flag === VENDOR_FLG
      ? item?.order_process_status === ORDER_STATUS.CANCELLED
        ? false
        : item?.is_show_button === "Y"
        ? true
        : false
      : true;
  }, [item]);

  const showDownloadButton = useMemo(() => {
    return (
      item?.vendor_flag !== OrderHistory.VENDOR_FLG ||
      (item?.vendor_flag === OrderHistory.VENDOR_FLG &&
        item?.order_process_status === OrderHistory.ORDER_STATUS.COMPLETED)
    );
  }, [item]);

  const checkOrderStatus = (orderStatus: string) => {
    if (
      orderStatus === ORDER_STATUS.RESCHEDULED ||
      orderStatus === ORDER_STATUS.BOOKED ||
      orderStatus === ORDER_STATUS.PENDING
    ) {
      return ORDER_STATUS.UPCOMING;
    }
    return orderStatus;
  };

  const orderProcessStatus = useMemo(() => {
    if (!!item?.refund_status) {
      return item?.refund_status === REFUND_STATUS.IN_PROGRESS
        ? CUSTOME_STATUS.REFUND_IN_PROGRESS
        : `Refund ${item.refund_status}`;
    } else {
      return checkOrderStatus(item?.order_process_status);
    }
  }, [item]);

  const amountTxt = useMemo(() => {
    return item?.refund_status === REFUND_STATUS.COMPLETED
      ? "Amount Refunded"
      : "Amount Paid";
  }, [item]);

  const getStatusColor = (orderStatus: string) => {
    const normalizedStatus = orderStatus.toUpperCase();
    const status = checkOrderStatus(normalizedStatus);
    if (
      status === CUSTOME_STATUS.REFUND_COMPLETED ||
      status === ORDER_STATUS.COMPLETED
    ) {
      return "bg-green-100 text-green-800 border-green-200";
    } else if (
      status === ORDER_STATUS.UPCOMING ||
      status === ORDER_STATUS.BOOKED ||
      status === CUSTOME_STATUS.REFUND_IN_PROGRESS ||
      status === CUSTOME_STATUS.REFUND_IN_PROGRESS.toUpperCase()
    ) {
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (status === ORDER_STATUS.CANCELLED) {
      return "bg-red-100 text-red-800 border-red-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderAmount = () => {
    let amount;
    if (!!item.bcp_test_price_data?.bcp_final_amount_to_pay) {
      if ((item?.bcp_test_price_data?.bcpFinalAmountToPay || 0) > 0) {
        amount = `${item?.bcp_test_price_data?.bcpFinalAmountToPay}`;
      }
      amount = "FREE";
    } else {
      amount = item?.final_payable_amount;
    }

    return amount === "FREE" ? (
      <span className="text-lg font-semibold text-green-600">FREE</span>
    ) : (
      <span className="text-lg font-semibold text-gray-900">
        {formatCurrency(Number(amount))}
      </span>
    );
  };

  return (
    <Card
      onClick={() => onPressItem(item)}
      stackTitleAndDescription={true}
      rightIcon={
        showMenuButton && (
          <div onClick={(e) => e.stopPropagation()}>
            <Popover label={<EllipsisVertical className="w-4 h-4" />}>
              <ul className="w-full">
                <li className="w-full block">
                  <div
                    //type="button"
                    className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/app/${supportProgramKey}/book-lab-test/reason-list/reschedule`,
                        { state: { orderDetails: item } }
                      );
                      //onEditAddress(address);
                    }}
                  >
                    <CalendarClock className="w-4 h-4" /> Reschedule Lab Test
                  </div>
                </li>
                <li className="w-full block">
                  <div
                    //type="button"
                    className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/app/${supportProgramKey}/book-lab-test/reason-list/cancel`,
                        { state: { orderDetails: item } }
                      );
                      //onDeleteAddress(address.patient_address_rel_id);
                    }}
                  >
                    <XCircle className="w-4 h-4" /> Cancel Lab Test
                  </div>
                </li>
              </ul>
            </Popover>
          </div>
        )
      }
      //className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 mx-4 cursor-pointer hover:shadow-md transition-shadow"
    >
      {/* Header with Icon, Status, and Menu */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            {/* <span className="text-blue-600 font-semibold text-sm">LT</span> */}
            <FlaskConical className="w-5 h-5 text-gray-700" />
          </div>
          {orderProcessStatus && (
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                orderProcessStatus
              )}`}
            >
              {orderProcessStatus}
            </span>
          )}
        </div>
        {/* {showMenuButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPressMenuButton(item);
            }}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MoreVerticalIcon className="w-5 h-5 text-gray-500" />
          </button>
        )} */}
      </div>

      {/* Test Name and Booking Details */}
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {item?.name}
        </h3>
        {item?.appointment_date && (
          <p className="text-sm text-gray-600">
            Booked for{" "}
            <span className="font-medium text-gray-800">
              {moment(item?.appointment_date, "YYYY-MM-DD").format(
                "DD/MM/YYYY"
              )}
              {item?.slot_time && (
                <>
                  {" "}
                  (
                  {moment(item?.slot_time?.split("-")[0], "HH:mm").format(
                    "HH:mm"
                  )}{" "}
                  -
                  {moment(item?.slot_time?.split("-")[1], "HH:mm").format(
                    "HH:mm"
                  )}
                  )
                </>
              )}
            </span>
          </p>
        )}
      </div>

      {/* Separator */}
      <div className="border-t border-gray-200 pt-3 mb-3">
        <p className="text-xs text-gray-500 mb-2">{amountTxt}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {renderAmount()}
          <span className="text-xs text-gray-500">
            - On{" "}
            {moment(item?.created_at, "YYYY-MM-DD HH:mm:ss").format(
              "DD/MM/YYYY [at] hh:mma"
            )}
          </span>
        </div>
      </div>

      {/* Download Report Button */}
      {showDownloadButton && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDownloadReportPress(item);
          }}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors font-medium"
        >
          <DownloadIcon className="w-4 h-4" />
          Download Report
        </button>
      )}
    </Card>
  );
};

export default OrderHistoryItem;
